import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Tag, Switch, Modal, Form, Input, message,
  Row, Col, Statistic, Alert, Divider, Space, Tooltip, Badge, Steps
} from 'antd';
import {
  ApiOutlined, CheckCircleOutlined, LinkOutlined, KeyOutlined,
  SafetyOutlined, BankOutlined, TeamOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';
import BiometricIntegration from './BiometricIntegration';

const INTEGRATIONS = [
  {
    type: 'quickbooks', name: 'QuickBooks Online', logo: '📊', category: 'accounting',
    description: 'Sync payroll journals to QuickBooks as journal entries automatically.',
    color: '#2ca01c',
    fields: [
      { key: 'client_id', label: 'Client ID', placeholder: 'Your QuickBooks App Client ID', secret: false },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'Your QuickBooks App Client Secret', secret: true },
      { key: 'company_id', label: 'Company ID (Realm ID)', placeholder: 'Found in your QuickBooks URL', secret: false },
    ],
    help: 'Get credentials at developer.intuit.com → Create App → Keys & OAuth',
    help_url: 'https://developer.intuit.com',
  },
  {
    type: 'sage', name: 'Sage Business Cloud', logo: '📗', category: 'accounting',
    description: 'Export payroll data in Sage-compatible format for seamless reconciliation.',
    color: '#00dc82',
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Sage API Key', secret: true },
      { key: 'company_code', label: 'Company Code', placeholder: 'Your Sage Company Code', secret: false },
    ],
    help: 'Get API key from Sage Developer Portal → My Apps',
    help_url: 'https://developerselfservice.sageone.com',
  },
  {
    type: 'firs', name: 'FIRS TaxPro Max', logo: '🏛️', category: 'statutory',
    description: 'File monthly PAYE returns directly to FIRS TaxPro Max. Due by 10th of each month.',
    color: '#1890ff',
    fields: [
      { key: 'tin', label: 'Tax Identification Number (TIN)', placeholder: 'e.g. 12345678-0001', secret: false },
      { key: 'taxpro_username', label: 'TaxPro Max Username', placeholder: 'Your FIRS portal username', secret: false },
      { key: 'taxpro_password', label: 'TaxPro Max Password', placeholder: 'Your FIRS portal password', secret: true },
    ],
    help: 'Register/login at taxpromax.firs.gov.ng to get credentials',
    help_url: 'https://taxpromax.firs.gov.ng',
  },
  {
    type: 'pension_pfa', name: 'Pension Fund Administrator', logo: '🏦', category: 'statutory',
    description: 'Remit employee and employer pension contributions to your PFA within 7 working days.',
    color: '#722ed1',
    fields: [
      { key: 'pfa_name', label: 'PFA Name', placeholder: 'e.g. Stanbic IBTC Pension, ARM Pension', secret: false },
      { key: 'employer_code', label: 'Employer Code', placeholder: 'Assigned by your PFA', secret: false },
      { key: 'api_key', label: 'API Key / Token', placeholder: 'API key from your PFA portal', secret: true },
    ],
    help: 'Contact your registered PFA directly for API access credentials',
    help_url: 'https://pencom.gov.ng',
  },
  {
    type: 'nhis', name: 'NHIS / HMO', logo: '🏥', category: 'statutory',
    description: 'Submit NHIS health insurance contributions monthly via your registered HMO.',
    color: '#eb2f96',
    fields: [
      { key: 'employer_code', label: 'Employer Code', placeholder: 'NHIS Employer Registration Code', secret: false },
      { key: 'hmo_name', label: 'HMO Provider', placeholder: 'e.g. Hygeia, AXA Mansard, Redcare', secret: false },
      { key: 'api_key', label: 'HMO API Key (if available)', placeholder: 'Optional — from your HMO portal', secret: true },
    ],
    help: 'Register at nhis.gov.ng and contact your HMO for API access',
    help_url: 'https://nhis.gov.ng',
  },
  {
    type: 'biometric', name: 'Biometric / Access Control', logo: '👆', category: 'attendance',
    description: 'Import attendance records from ZKTeco, Virdi, or any CSV-export biometric device.',
    color: '#fa8c16',
    fields: [
      { key: 'device_model', label: 'Device Model', placeholder: 'e.g. ZKTeco K40, Virdi AC2100', secret: false },
      { key: 'api_endpoint', label: 'Device API Endpoint (if network-enabled)', placeholder: 'http://192.168.1.x:4370', secret: false },
      { key: 'api_key', label: 'API Key / Password (if required)', placeholder: 'Device access password', secret: true },
    ],
    help: 'Network-enabled devices support direct API pull. Otherwise use CSV export import.',
    help_url: 'https://www.zkteco.com',
  },
];

const IntegrationsHub: React.FC = () => {
  const [saved, setSaved] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [configModal, setConfigModal] = useState(false);

  const fetchBMPStatus = async () => {
    const r = await apiService.getNexusBMPStatus();
    if (r.success) setBmpStatus(r.data);
  };

  React.useEffect(() => { fetchBMPStatus(); }, []);

  const handleBMPSync = async (action: string) => {
    setBmpSyncing(action);
    let r: any;
    if (action === 'employees') r = await apiService.syncEmployeesToBMP();
    else if (action === 'payroll') r = await apiService.syncPayrollToBMP(bmpPeriod);
    else if (action === 'journals') {
      r = await apiService.getBMPJournalEntries();
      if (r.success) setBmpJournals(r.data || []);
      setBmpSyncing(null);
      return;
    } else r = { success: false };
    setBmpSyncing(null);
    if (r.success) { message.success(r.message || 'Sync complete'); fetchBMPStatus(); }
    else message.error(r.error || 'Sync failed');
  };
  const [bmpStatus, setBmpStatus] = useState<any>(null);
  const [bmpJournals, setBmpJournals] = useState<any[]>([]);
  const [bmpSyncing, setBmpSyncing] = useState<string | null>(null);
  const [bmpPeriod, setBmpPeriod] = useState(new Date().toISOString().slice(0,7));
  const [selected, setSelected] = useState<any>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [configForm] = Form.useForm();

  useEffect(() => { fetchSaved(); }, []);

  const fetchSaved = async () => {
    setLoading(true);
    try {
      const r = await apiService.getIntegrationsCatalogue();
      if (r.success) {
        const map: Record<string, any> = {};
        (r.data || []).forEach((item: any) => { map[item.type] = item; });
        setSaved(map);
      }
    } finally { setLoading(false); }
  };

  const openConfig = (integration: any) => {
    setSelected(integration);
    configForm.resetFields();
    const existing = saved[integration.type];
    if (existing?.config) {
      try {
        const cfg = typeof existing.config === 'string' ? JSON.parse(existing.config) : existing.config;
        configForm.setFieldsValue(cfg);
      } catch {}
    }
    setConfigModal(true);
  };

  const handleSave = async (values: any) => {
    const r = await apiService.saveIntegration({
      integration_type: selected.type,
      display_name: selected.name,
      config: JSON.stringify(values),
      is_enabled: 1,
    });
    if (r.success) {
      message.success(`${selected.name} configuration saved and activated`);
      setConfigModal(false);
      fetchSaved();
    } else message.error(r.error || 'Failed to save');
  };

  const handleToggle = async (integration: any, enabled: boolean) => {
    const r = await apiService.saveIntegration({
      integration_type: integration.type,
      display_name: integration.name,
      is_enabled: enabled ? 1 : 0,
    });
    if (r.success) {
      message.success(enabled ? `${integration.name} activated` : `${integration.name} deactivated`);
      fetchSaved();
    } else message.error(r.error || 'Failed');
  };

  const handleTest = async (type: string) => {
    const cfg = saved[type];
    if (!cfg?.is_enabled) { message.warning('Please configure and activate this integration first'); return; }
    setTesting(type);
    try {
      const r = await apiService.testIntegration(type);
      if (r.success) message.success(r.message || '✅ Connection test successful');
      else message.warning(r.error || 'Connection test failed — check your credentials');
    } finally { setTesting(null); }
  };

  const categories = [
    { key: 'accounting', title: '📊 Accounting Software', icon: <BankOutlined /> },
    { key: 'statutory', title: '🏛️ Nigerian Statutory Bodies', icon: <SafetyOutlined /> },
    { key: 'attendance', title: '👆 Attendance & Biometrics', icon: <ClockCircleOutlined /> },
  ];

  const connected = Object.values(saved).filter((s: any) => s.is_enabled).length;

  return (
    <div style={{ padding: 24 }}>
      <h1>Integrations Hub</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>Connect Nexus HR with your accounting software and Nigerian statutory systems. Click <strong>Configure</strong> on any integration, paste your API credentials, and activate.</p>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}><Card><Statistic title="Available Integrations" value={INTEGRATIONS.length} prefix={<ApiOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Active" value={connected} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Accounting" value={INTEGRATIONS.filter(i => i.category==='accounting').length} /></Card></Col>
        <Col span={6}><Card><Statistic title="Statutory" value={INTEGRATIONS.filter(i => i.category==='statutory').length} /></Card></Col>
      </Row>

      <Alert style={{ marginBottom: 24 }} type="info" showIcon
        message="How to connect an integration"
        description={
          <Steps size="small" style={{ marginTop: 8 }} items={[
            { title: 'Get API credentials from the provider portal', status: 'process' },
            { title: 'Click Configure on the integration card', status: 'process' },
            { title: 'Paste your credentials and click Save & Activate', status: 'process' },
            { title: 'Click Test to verify the connection', status: 'process' },
          ]} />
        } />

      {categories.map(cat => (
        <Card key={cat.key} title={cat.title} style={{ marginBottom: 20 }}>
          <Row gutter={16}>
            {INTEGRATIONS.filter(i => i.category === cat.key).map(integration => {
              const s = saved[integration.type];
              const isActive = !!s?.is_enabled;
              return (
                <Col span={8} key={integration.type} style={{ marginBottom: 12 }}>
                  <Card size="small"
                    style={{ border: `2px solid ${isActive ? integration.color : '#f0f0f0'}`, borderRadius: 8, transition: 'border 0.3s' }}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 20 }}>{integration.logo}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{integration.name}</div>
                          {isActive
                            ? <Tag color="success" style={{ fontSize: 10 }}>✅ Active</Tag>
                            : <Tag color="default" style={{ fontSize: 10 }}>Not configured</Tag>
                          }
                        </div>
                      </div>
                    }
                    extra={
                      <Switch size="small" checked={isActive}
                        onChange={v => handleToggle(integration, v)}
                        checkedChildren="ON" unCheckedChildren="OFF"
                        disabled={!s?.config} />
                    }>
                    <p style={{ fontSize: 12, color: '#666', minHeight: 40, margin: '0 0 12px' }}>{integration.description}</p>
                    <Divider style={{ margin: '8px 0' }} />
                    <Space>
                      <Button size="small" type="primary" icon={<KeyOutlined />} onClick={() => openConfig(integration)}>
                        {s?.config ? 'Edit Config' : 'Configure'}
                      </Button>
                      <Button size="small" icon={<LinkOutlined />}
                        loading={testing === integration.type}
                        disabled={!isActive}
                        onClick={() => handleTest(integration.type)}>
                        Test
                      </Button>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      ))}

      {/* Provider Notes */}
      <Card title="🏢 Nexus Business Management — Live Sync"
        extra={<Button size="small" onClick={fetchBMPStatus}>Refresh Status</Button>}>
        {bmpStatus ? (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}><Card size="small"><Statistic title="HR Employees" value={bmpStatus.hr_employees} /></Card></Col>
              <Col span={6}><Card size="small"><Statistic title="BMP Users" value={bmpStatus.bmp_users} /></Card></Col>
              <Col span={6}><Card size="small"><Statistic title="Approved Payroll" value={bmpStatus.approved_payroll} /></Card></Col>
              <Col span={6}><Card size="small"><Statistic title="Journal Entries" value={bmpStatus.journal_entries} /></Card></Col>
            </Row>
            {bmpStatus.last_sync && <p style={{ color: '#999', fontSize: 12 }}>Last sync: {bmpStatus.last_sync}</p>}
            <Space wrap>
              <Button type="primary" loading={bmpSyncing==='employees'} onClick={() => handleBMPSync('employees')}>
                🔄 Sync Employees → BMP
              </Button>
              <Input.Group compact style={{ display: 'inline-flex' }}>
                <Input type="month" value={bmpPeriod} onChange={e => setBmpPeriod(e.target.value)} style={{ width: 150 }} />
                <Button loading={bmpSyncing==='payroll'} onClick={() => handleBMPSync('payroll')}>
                  📊 Post Payroll Journal
                </Button>
              </Input.Group>
              <Button loading={bmpSyncing==='journals'} onClick={() => handleBMPSync('journals')}>
                📋 View BMP Journals
              </Button>
            </Space>
            {bmpJournals.length > 0 && (
              <Table size="small" style={{ marginTop: 16 }} dataSource={bmpJournals} rowKey="id" pagination={{ pageSize: 5 }}
                columns={[
                  { title: 'Reference', dataIndex: 'reference_number', width: 160 },
                  { title: 'Account', dataIndex: 'account_name' },
                  { title: 'Debit', dataIndex: 'debit_amount', render: (v: number) => v > 0 ? `NGN ${Number(v).toLocaleString()}` : '—' },
                  { title: 'Credit', dataIndex: 'credit_amount', render: (v: number) => v > 0 ? `NGN ${Number(v).toLocaleString()}` : '—' },
                  { title: 'Date', dataIndex: 'entry_date' },
                ]} />
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
            <p>Enable Nexus BMP integration in the integrations list above to activate sync.</p>
          </div>
        )}
      </Card>

      <Card title="📋 Where to Get Credentials">
        <Row gutter={16}>
          {INTEGRATIONS.map(i => (
            <Col span={8} key={i.type} style={{ marginBottom: 12 }}>
              <Card size="small" title={<span>{i.logo} {i.name}</span>}>
                <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>{i.help}</p>
                <Button size="small" type="link" href={i.help_url} target="_blank" icon={<LinkOutlined />} style={{ padding: 0 }}>
                  Open Portal ↗
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Config Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24 }}>{selected?.logo}</span>
            <div>
              <div style={{ fontWeight: 700 }}>{selected?.name}</div>
              <div style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>Enter your API credentials below</div>
            </div>
          </div>
        }
        open={configModal}
        onCancel={() => { setConfigModal(false); configForm.resetFields(); }}
        footer={null} width={520}>

        <Alert type="info" showIcon style={{ marginBottom: 20 }}
          message={selected?.help}
          description={
            <Button type="link" href={selected?.help_url} target="_blank" style={{ padding: 0, fontSize: 12 }}>
              {selected?.help_url} ↗
            </Button>
          } />

        {selected?.type === 'biometric' && <BiometricIntegration />}
        <Form form={configForm} layout="vertical" onFinish={handleSave}>
          {selected?.fields?.map((field: any) => (
            <Form.Item key={field.key} name={field.key} label={field.label}
              rules={[{ required: false }]}>
              {field.secret
                ? <Input.Password placeholder={field.placeholder} autoComplete="new-password" />
                : <Input placeholder={field.placeholder} />
              }
            </Form.Item>
          ))}
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message="Credentials are stored encrypted in the database and never exposed in the UI." />
          <Space>
            <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />}>
              Save & Activate
            </Button>
            <Button onClick={() => { setConfigModal(false); configForm.resetFields(); }}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default IntegrationsHub;
